import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { uiLocaleTag, uiCurrency, uiTimeZone, tUi } from '../i18n';

/**
 * Merge Tailwind classes with proper precedence
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency in the active tenant's currency/locale (defaults SEK / sv-SE).
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(uiLocaleTag(), {
    style: 'currency',
    currency: uiCurrency(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a date/time in the tenant's timezone, using the active locale.
 * Always goes through this (not the browser's local zone) so times render
 * consistently regardless of where the dashboard happens to be opened.
 */
export function formatInTz(date: string | Date, options: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(uiLocaleTag(), { ...options, timeZone: uiTimeZone() }).format(d);
}

/**
 * Format date in the active locale
 */
export function formatDate(date: string | Date): string {
  return formatInTz(date, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Format time in the active locale
 */
export function formatTime(date: string | Date): string {
  return formatInTz(date, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format date and time
 */
export function formatDateTime(date: string | Date): string {
  return formatInTz(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return tUi('justNow');
  if (diffInSeconds < 3600) return tUi('minutesAgo', { n: Math.floor(diffInSeconds / 60) });
  if (diffInSeconds < 86400) return tUi('hoursAgo', { n: Math.floor(diffInSeconds / 3600) });
  if (diffInSeconds < 604800) return tUi('daysAgo', { n: Math.floor(diffInSeconds / 86400) });

  return formatDate(d);
}

/**
 * Calculate distance between two coordinates using Haversine formula
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10; // Round to 1 decimal
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Format distance with Swedish unit
 */
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
}

/**
 * Validate Swedish phone number
 */
export function validatePhone(phone: string): boolean {
  // Swedish phone number regex (mobile and landline)
  const phoneRegex = /^(\+46|0)[\s-]?[\d\s-]{7,12}$/;
  return phoneRegex.test(phone);
}

/**
 * Validate email address
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Format phone number for display
 */
export function formatPhone(phone: string): string {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // Swedish format: 070-123 45 67
  if (digits.length === 10 && digits.startsWith('07')) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
  }
  
  return phone;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format status label for display
 */
export function formatStatus(status: string): string {
  const known = [
    'open', 'closed', 'booked', 'pending',
    'accepted', 'rejected', 'confirmed', 'completed', 'cancelled',
  ];
  return known.includes(status) ? tUi(`status.${status}`) : capitalize(status);
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    open: 'bg-blue-100 text-blue-800',
    closed: 'bg-gray-100 text-gray-800',
    booked: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}
